import { fromCognitoIdentityPool } from '@aws-sdk/credential-provider-cognito-identity';
import { logError } from './logger';

// Module-level cache to prevent multiple initializations
let cachedProvider = null;
let initializationPromise = null;

// This function initializes Cognito credentials and returns the provider.
// It's designed to be called once when the application loads.
const initializeCognito = async (identityPoolId) => {
  // Return cached provider if already initialized
  if (cachedProvider) {
    return cachedProvider;
  }

  // Return existing promise if initialization is in progress
  if (initializationPromise) {
    return initializationPromise;
  }

  if (!identityPoolId) {
    logError('IdentityPoolId is not provided. Cannot initialize Cognito.');
    // Return a provider that will always reject to prevent further issues.
    return async () => {
      throw new Error('IdentityPoolId not provided during Cognito initialization.');
    };
  }

  // Start initialization process - create promise immediately to prevent race conditions
  initializationPromise = (async () => {
    try {
      // Create a Cognito identity provider
      const cognitoIdentityProvider = fromCognitoIdentityPool({
        clientConfig: { region: import.meta.env.VITE_APP_AWS_REGION || 'us-east-1' },
        identityPoolId: identityPoolId,
      });

      // Cache the provider (don't call it immediately to avoid triggering API calls)
      cachedProvider = cognitoIdentityProvider;

      // Return the provider itself to be used by SignatureV4
      return cognitoIdentityProvider;
    } catch (err) {
      logError('Error creating AWS Cognito identity provider:', err);
      // Return a provider that will always reject
      return async () => {
        throw new Error(`Failed to initialize Cognito: ${err.message}`);
      };
    } finally {
      // Clear the promise reference when done
      initializationPromise = null;
    }
  })();

  return initializationPromise;
};
export default initializeCognito;
